import { useCallback, useEffect, useMemo, useState } from 'react';
import http, { coreApiUrl } from 'shared/utils/http';
import {
  ChannelViewChannelMessageFragment,
  GetChannelViewChannelMessagesDocument,
  SortOrder,
} from 'shared/types/graphql';
import useClinicUser from 'shared/hooks/useClinicUser';
import { HttpMethods } from 'shared/enums/HttpMethods';
import { useAuth } from 'shared/providers/AuthProvider';
import sortedUniqBy from 'lodash-es/sortedUniqBy';

interface ChannelMessagesResponse {
  findManyChannelMessage: ChannelViewChannelMessageFragment[];
  findManyChannelMessageCount: number;
}

interface UseChannelViewChannelMessagesProps {
  channelId?: string;
}

interface UseChannelViewChannelMessagesReturn {
  data: ChannelMessagesResponse | null;
  loading: boolean;
  error: Error | null;
  refetch: () => Promise<void> | undefined;
  fetchMore: (variables: { cursor: { id: string } }) => Promise<void> | undefined;
}

interface FetchMessagesVariables {
  where: { channelId: { equals: string } };
  orderBy: { createdAt: SortOrder };
  take: number;
  cursor?: { id: string };
  skip?: number;
  isFetchMore?: boolean;
}

export const useChannelViewChannelMessages = ({
  channelId,
}: UseChannelViewChannelMessagesProps): UseChannelViewChannelMessagesReturn => {
  const [messages, setMessages] = useState<Record<string, ChannelViewChannelMessageFragment> | null>(null);
  const [count, setCount] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const { clinicUser } = useClinicUser();
  const { getToken, isAuthenticated } = useAuth();

  const handleMergeNewMessages = useCallback((newMessages: ChannelViewChannelMessageFragment[]) => {
    const _newMessages: Record<string, ChannelViewChannelMessageFragment> = {};

    newMessages.forEach((item) => {
      if (item.id) {
        _newMessages[item.id] = item;
      }
    });

    setMessages((prevMessages) => {
      return {
        ...prevMessages,
        ..._newMessages,
      };
    });
  }, []);

  const fetchMessages = useCallback(
    async (variables: FetchMessagesVariables) => {
      try {
        setLoading(true);
        const token = await getToken();
        if (!token) {
          throw new Error('No authentication token available');
        }
        const response = await http<{ data: ChannelMessagesResponse }>({
          method: HttpMethods.Post,
          url: `${coreApiUrl}/graphql`,
          token,
          clinicUser,
          body: JSON.stringify({
            query: GetChannelViewChannelMessagesDocument.loc?.source.body,
            variables,
          }),
        });

        handleMergeNewMessages(response.data.findManyChannelMessage);
        setCount(response.data.findManyChannelMessageCount);
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Failed to fetch messages'));
      } finally {
        setLoading(false);
      }
    },
    [clinicUser, getToken, handleMergeNewMessages],
  );

  const refetch = useCallback(async () => {
    if (!channelId || !isAuthenticated) return;
    await fetchMessages({
      where: { channelId: { equals: channelId } },
      orderBy: { createdAt: SortOrder.Asc },
      take: -20,
    });
  }, [channelId, isAuthenticated, fetchMessages]);

  const fetchMore = useCallback(
    async (variables: { cursor: { id: string } }) => {
      if (!channelId || !isAuthenticated) return;

      await fetchMessages({
        where: { channelId: { equals: channelId } },
        orderBy: { createdAt: SortOrder.Asc },
        take: -20,
        cursor: variables.cursor,
        // Skip the cursor message itself
        skip: 1,
      });
    },
    [channelId, isAuthenticated, fetchMessages],
  );

  const filteredSortedUniqueMessages = useMemo(() => {
    if (!messages) return [];
    const channelMessages = Object.values(messages)
      .filter((message: ChannelViewChannelMessageFragment) => message?.channel?.id === channelId)
      .map((message: ChannelViewChannelMessageFragment) => {
        return {
          ...message,
          attributes: typeof message.attributes === 'string' ? JSON.parse(message.attributes) : message.attributes,
        };
      });

    return sortedUniqBy(channelMessages, (message: ChannelViewChannelMessageFragment) => message.id).sort(
      (a, b) => a.index - b.index,
    );
  }, [channelId, messages]);

  useEffect(() => {
    if (!channelId || !isAuthenticated || !clinicUser) return;
    fetchMessages({
      where: { channelId: { equals: channelId } },
      orderBy: { createdAt: SortOrder.Asc },
      take: -20,
    });
  }, [channelId, isAuthenticated, fetchMessages, clinicUser]);

  // Reset messages when channelId changes
  useEffect(() => {
    setMessages(null);
  }, [channelId]);

  return {
    data: {
      findManyChannelMessage: filteredSortedUniqueMessages,
      findManyChannelMessageCount: count || 0,
    },
    loading,
    error,
    refetch,
    fetchMore,
  };
};
